import { useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useOutsideClick } from "@script/script";
import type { GroupInfo } from "@vTypes";
import { images } from "@/assets/img/imagesMap";
import { useGroupSelection } from "@/hooks/useGroupSelection";
import {
  useIsAdmin,
  useIsGroupNurse,
  useLoginUser,
  useAuthActions,
} from "@/hooks/useAuth";
import { useGroupNurseGroupsQuery } from "@/hooks/useGroupNurseGroupsQuery";
// import httpClient from "@/config/axios"; // TODO: 토큰 변경 API 구현 시 사용
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/toast/CustomToast";
import httpClient from "@/config/axios";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { compareGroupNames } from "@/utils/sort-util";
import { useNavigate } from "react-router-dom";

type Props = {
  onClickAdd: () => void;
  openAddGroup: boolean;
  showAddButton?: boolean; // nav 전용 스타일을 위해서 추가
  nav?: boolean; // 그룹 메뉴 안보이게 하기 위해서 추가
  hiddenGroup?: boolean;
  onEdit?: (group: GroupInfo) => void;
};

export default function GroupList({
  onClickAdd,
  nav = false,
  hiddenGroup = false,
  onEdit,
}: Props) {
  const [isGroupListOpend, setIsGroupListOpend] = useState<boolean>(false);
  const groupListRef = useRef<HTMLDivElement | null>(null);
  const {
    groups,
    selectedGroupId,
    setSelectedGroupId: onSelect,
  } = useGroupSelection();
  const isAdmin = useIsAdmin();
  const isGroupNurse = useIsGroupNurse();
  const loginUser = useLoginUser();
  const { setLoginUser } = useAuthActions();

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  useOutsideClick(
    groupListRef as unknown as React.RefObject<HTMLElement>,
    () => {
      setIsGroupListOpend(false);
    }
  );

  const { data: groupNurseGroups } = useGroupNurseGroupsQuery(isGroupNurse);
  const rawGroupDatas = isAdmin ? groups : groupNurseGroups;

  // group_name 기준으로 가나다순(한글 우선), 그 다음 숫자순 정렬
  const groupDatas = useMemo(() => {
    if (!rawGroupDatas) return rawGroupDatas;

    return [...rawGroupDatas].sort((a, b) => {
      return compareGroupNames(a.group_name || "", b.group_name || "");
    });
  }, [rawGroupDatas]);

  // 여기서 그룹 네임 반환함
  const selectedName = (() => {
    const targetGroups = groupDatas ?? [];
    const found = targetGroups.find((g) => g.group_id === selectedGroupId);
    return found
      ? found.group_name
      : (targetGroups[0]?.group_name ?? "그룹 없음");
  })();

  // Admin
  const handleGroupSelect = (group_id: string) => {
    queryClient.clear();
    onSelect(group_id);
    setIsGroupListOpend(false);
    // 그룹 변경 시 루트로 이동
    navigate("/");
  };

  const { mutateAsync: changeToken, isPending: isChangingToken } = useMutation({
    mutationFn: async (group_id: string) => {
      return await httpClient.post<{
        message: string;
        group_id: string;
        group_name: string;
      }>("/auth/switch-group", {
        target_group_id: group_id,
      });
    },
    onSuccess: (data) => {
      onSelect(data.group_id);

      if (loginUser) {
        setLoginUser({
          ...loginUser,
          group_id: data.group_id,
          mb_part_name: data.group_name,
        });
      }

      queryClient.clear();
      setIsGroupListOpend(false);
      toast.success("그룹을 전환하였습니다.");
      // 그룹 변경 시 루트로 이동
      navigate("/");
    },
  });

  // 그룹관리자 - 이땐 Token 변경해야함
  // Token 변경 프로세스
  const handleGroupNurseSelect = async (group_id: string) => {
    await changeToken(group_id);
  };

  const role = (() => {
    if (isAdmin) return "ADMIN";
    else if (isGroupNurse) return "HN";
    else return "NURSE";
  })();

  const portalRoot = document.getElementById("change-group-portal");

  const loadingOverlay =
    isChangingToken && portalRoot
      ? createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30  "
            style={{
              backdropFilter: "blur(2px)",
            }}
          >
            <div className="flex flex-col items-center justify-center px-6 py-4 rounded-lg">
              <Alert variant="default" className="shadow-2xl">
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertTitle>그룹 변경 중입니다.</AlertTitle>
                <AlertDescription>잠시만 기다려주세요.</AlertDescription>
              </Alert>
            </div>
          </div>,
          portalRoot
        )
      : null;

  return (
    <>
      {/* Portal */}
      {loadingOverlay}
      {!hiddenGroup && (
        <div
          className="mr-auto"
          style={{ display: "flex", gap: "8px", alignItems: "center" }}
        >
          <div
            id="bookmarks-container"
            className="component_button__bookmarkWrap"
            ref={groupListRef}
          >
            <button
              type="button"
              className={`component_button__bookmark conf_selectType--button ${nav && "!rounded-[10px] !w-[187px] !text-[17px] !text-left !border-sig !border-2"}`}
              style={{ width: "160px", boxShadow: "none" }}
              onClick={() => {
                setIsGroupListOpend((prev) => !prev);
              }}
            >
              <span
                className={`${nav && "text-sig font-semibold"}`}
                style={{ flexGrow: "1" }}
              >
                {selectedName}
              </span>
            </button>
            {isGroupListOpend && (
              <div className="component_button__bookmarkList !mt-2 !py-1">
                <div className="component_button__bookmarkList_scrollBox thin-scrollbar">
                  {isAdmin && (
                    <button
                      type="button"
                      className=" font-medium block w-full font-pretendard
                      whitespace-nowrap  text-ellipsis z-[1] !text-[17px] text-[#0E59C6] ] !pr-[8px] !py-[6px] hover:bg-[#F0F0F2]"
                      onClick={() => {
                        onClickAdd();
                        setIsGroupListOpend(false);
                      }}
                    >
                      <span className="font-pretendard">+부서(병동)추가</span>
                    </button>
                  )}

                  {(groupDatas ?? []).map((item) => {
                    const isActive = item.group_id === selectedGroupId;

                    return (
                      <button
                        type="button"
                        key={item.group_id}
                        className={`hover:bg-[#F0F0F2] block w-full font-pretendard font-bold text-sm text-gray-800 px-[26px] py-3 whitespace-nowrap  text-ellipsis z-[1]" ${isActive ? "bg-[#dbe9ff]" : ""} 
                        !font-normal py-1! text-base! !flex !items-center justify-between !pl-[21px] !pr-[8px]`}
                        onClick={() => {
                          switch (role) {
                            case "ADMIN":
                              handleGroupSelect(item.group_id);
                              break;
                            case "HN":
                              handleGroupNurseSelect(item.group_id);
                              break;
                          }
                        }}
                      >
                        <div className="truncate">
                          <span className="font-pretendard ">
                            {item.group_name}
                          </span>
                        </div>
                        {isAdmin && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit?.(item);
                              setIsGroupListOpend(false);
                            }}
                            className="shrink-0"
                          >
                            <img
                              src={images.icon_pencil}
                              alt=""
                              aria-hidden="true"
                              className="hover:opacity-70"
                            />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
